// client/src/utils/outsideSearch.js — "Outside sites" search links (owner, 17 Oct 2026).
//
// Until the MLS/IDX feed is on, agents search Zillow, Realtor.com, Apartments.com, ForRent, LoopNet,
// Crexi, CityFeet and CommercialCafe themselves. The portal only builds each site's search
// address from one set of criteria and opens it in the agent's browser: a person searches,
// the server never fetches these sites (they refuse automated visitors; lib/fetchGuard).
//
// Each site has a URL template per deal (sale / rent). Placeholders:
//   {city} Oak Park   {city_slug} oak-park   {City_dash} Oak-Park   {City_ST} Oak-Park_IL
//   {st} IL   {st_lower} il   {beds} {baths} {min} {max}   {min_na} {max_na} ("na" when empty)
// A part in [square brackets] is dropped when every placeholder in it is empty, so filters
// the agent left blank vanish from the address. The managing broker edits the templates
// (Property Search → Outside sites → Edit links) when a site changes its addresses.

export const DEFAULT_SITES = [
    { key: 'zillow', label: 'Zillow', kind: 'residential',
      sale: 'https://www.zillow.com/{city_slug}-{st_lower}/', rent: 'https://www.zillow.com/{city_slug}-{st_lower}/rentals/' },
    { key: 'realtor', label: 'Realtor.com', kind: 'residential',
      sale: 'https://www.realtor.com/realestateandhomes-search/{City_ST}[/beds-{beds}][/baths-{baths}][/price-{min_na}-{max_na}]',
      rent: 'https://www.realtor.com/apartments/{City_ST}[/beds-{beds}][/price-{min_na}-{max_na}]' },
    { key: 'apartments', label: 'Apartments.com', kind: 'residential',
      sale: '', rent: 'https://www.apartments.com/{city_slug}-{st_lower}/' },
    // ForRent (25 Oct 2026). Its own town pages need a metro name in the address
    // (forrent.com/find/IL/metro-…/…) that could not be verified from here, so the link is a
    // Google search limited to forrent.com for the town — always lands on ForRent's pages.
    // [verify] swap in the native town address once checked in a browser (Edit links).
    { key: 'forrent', label: 'ForRent', kind: 'residential',
      sale: '', rent: 'https://www.google.com/search?q=site%3Aforrent.com+apartments+for+rent+{city}+{st}' },
    { key: 'loopnet', label: 'LoopNet', kind: 'commercial',
      sale: 'https://www.loopnet.com/search/commercial-real-estate/{city_slug}-{st_lower}/for-sale/',
      rent: 'https://www.loopnet.com/search/commercial-real-estate/{city_slug}-{st_lower}/for-lease/' },
    { key: 'crexi', label: 'Crexi', kind: 'commercial',
      sale: 'https://www.crexi.com/properties/{st}/{City_dash}', rent: 'https://www.crexi.com/lease/properties/{st}/{City_dash}' },
    { key: 'cityfeet', label: 'CityFeet', kind: 'commercial',
      sale: 'https://www.cityfeet.com/cont/{city_slug}-{st_lower}/commercial-real-estate-for-sale',
      rent: 'https://www.cityfeet.com/cont/{city_slug}-{st_lower}/commercial-real-estate-for-lease' },
    { key: 'commercialcafe', label: 'CommercialCafe', kind: 'commercial',
      sale: '', rent: 'https://www.commercialcafe.com/office-space-for-rent/us/{st_lower}/{city_slug}/' },
];

const titleDash = (s) => s.trim().split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('-');
const digits = (v) => String(v ?? '').replace(/[^\d]/g, '');

export function placeholders(c) {
    const city = String(c.city || '').trim();
    const st = String(c.state || '').trim().toUpperCase().slice(0, 2);
    const v = {
        city: encodeURIComponent(city),
        city_slug: city.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        City_dash: city ? titleDash(city) : '',
        City_ST: city && st ? `${titleDash(city)}_${st}` : '',
        st, st_lower: st.toLowerCase(),
        beds: digits(c.beds), baths: digits(c.baths), min: digits(c.min), max: digits(c.max),
    };
    return v;
}

// Fill one template; '' when it needs a value the agent did not give (city/state).
export function buildUrl(template, criteria) {
    if (!template) return '';
    const v = placeholders(criteria);
    const value = (name) => {
        if (name.endsWith('_na')) return v[name.slice(0, -3)] || 'na';
        return v[name] ?? '';
    };
    const isEmpty = (name) => !(v[name.replace(/_na$/, '')] || '');
    // Optional parts first.
    let out = template.replace(/\[([^\]]*)\]/g, (_, part) => {
        const names = [...part.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);
        if (names.length && names.every(isEmpty)) return '';
        return part.replace(/\{(\w+)\}/g, (__, n) => value(n));
    });
    let missing = false;
    out = out.replace(/\{(\w+)\}/g, (_, n) => { const x = value(n); if (!x) missing = true; return x; });
    return missing ? '' : out;
}

// Stored templates over the defaults; unknown keys from storage are kept (sites the broker added).
// A standard site added after the links were last saved (ForRent, 25 Oct 2026) is appended, so
// saving the list once does not hide every site added later. (There is no "remove a site".)
export function mergeSites(saved) {
    if (!Array.isArray(saved) || !saved.length) return DEFAULT_SITES;
    const kept = saved.filter((s) => s && s.key && s.label);
    const have = new Set(kept.map((s) => s.key));
    return [...kept, ...DEFAULT_SITES.filter((s) => !have.has(s.key))];
}
